import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styles from '../Sidebar/Sidebar.module.css';
import logoLightVertical from "../../assets/img/logo-light-vertical.png";
import {
  Dashboard,
  ViewKanban,
  FormatListBulleted,
  Agriculture,
  AccountBalance,
  PeopleAlt,
  AccountBox,
  Apps,
  Close,
} from "@mui/icons-material";

// ─── Itens principais do menu ──────────────────────────────────────────────────
const mainMenuItems = [
  { name: 'Dashboard',  path: '/agro/dashboard',  icon: <Dashboard /> },
  { name: 'Financeiro', path: '/agro/financeiro',  icon: <AccountBalance /> },
  { name: 'Kanban',     path: '/agro/kanban',      icon: <ViewKanban /> },
  { name: 'Eventos',    path: '/agro/eventos',     icon: <FormatListBulleted /> },
];

// ─── Itens do submenu "Cadastros" ─────────────────────────────────────────────
const cadastrosItems = [
  {
    name: 'Fazenda',
    path: '/agro/fazenda',
    icon: <Agriculture />,
    emoji: '🌾',
    desc: 'Áreas, veículos e culturas',
  },
  {
    name: 'Clientes',
    path: '/agro/customers',
    icon: <PeopleAlt />,
    emoji: '👥',
    desc: 'Gerenciar clientes',
  },
  {
    name: 'Fornecedores',
    path: '/agro/suppliers',
    icon: <AccountBox />,
    emoji: '🏭',
    desc: 'Gerenciar fornecedores',
  },
];

const AgroSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentModule = localStorage.getItem("modulo") || "agro";

  // Itens do drawer filtrados por permissão
  const filteredCadastros = useMemo(() => {
    return cadastrosItems;
  }, []);

  // Sidebar desktop
  const sidebarItems = useMemo(() => {
    return [...mainMenuItems, ...cadastrosItems];
  }, []);

  // Verifica se alguma rota de cadastros está ativa (para highlight do botão)
  const isCadastrosActive = cadastrosItems.some(i =>
    location.pathname.startsWith(i.path)
  );

  const handleCadastroNav = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      <style>{`
        .agro-bottom-nav-wrap { display: none; }
        .agro-drawer-overlay { display: none; }
        @media (max-width: 768px) {
          .agro-sidebar-wrap    { display: none !important; }
          .agro-bottom-nav-wrap { display: flex; }
          .agro-drawer-overlay  { display: block; }
        }
      `}</style>

      {/* ── Sidebar lateral (desktop) ─────────────────────────────────────── */}
      <aside className={`${styles.containerMenuSide} agro-sidebar-wrap`}>
        <div className={styles.logoContainer}>
          <img src={logoLightVertical} alt="Inteagro" className={styles.logoImg} />
        </div>
        <div className={styles.MenuSideItens}>
          {sidebarItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `${styles.MenuSideItem} ${styles.selected}`
                  : styles.MenuSideItem
              }
            >
              <div className={styles.MenuSideHidden}>
                <div className={styles.MenuSideItemImg}>{item.icon}</div>
                <p>{item.name}</p>
              </div>
            </NavLink>
          ))}
        </div>
      </aside>

      {/* ── Drawer de Cadastros (mobile) ──────────────────────────────────── */}
      {drawerOpen && (
        <div
          className="agro-drawer-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.45)',
          }}
          onClick={() => setDrawerOpen(false)}
        >
          <div
            style={{
              position: 'absolute', bottom: 60, left: 0, right: 0,
              background: '#fff',
              borderRadius: '20px 20px 0 0',
              padding: '20px 16px 16px',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do drawer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: '#222' }}>Cadastros</span>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#888' }}
              >
                <Close />
              </button>
            </div>

            {/* Grid de opções */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}>
              {filteredCadastros.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => handleCadastroNav(item.path)}
                    style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      gap: 6, padding: '14px 8px',
                      background: isActive ? '#e8f4ef' : '#f8f9fa',
                      border: isActive ? '1.5px solid #00b37e' : '1.5px solid transparent',
                      borderRadius: 14,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: '1.6rem' }}>{item.emoji}</span>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 600,
                      color: isActive ? '#00b37e' : '#444',
                      textAlign: 'center',
                    }}>
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom Navigation (mobile) ────────────────────────────────────── */}
      <nav
        className="agro-bottom-nav-wrap"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: 60,
          background: '#ffffff',
          borderTop: '1px solid #e8ede9',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.07)',
          alignItems: 'stretch',
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* 4 itens principais */}
        {mainMenuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 2, textDecoration: 'none',
                color: isActive ? '#00b37e' : '#9aada6',
                fontSize: '0.6rem',
                fontWeight: isActive ? 700 : 500,
                fontFamily: 'sans-serif',
                position: 'relative', paddingBottom: 2,
              }}
            >
              <span style={{
                position: 'absolute', top: 0, left: '50%',
                transform: 'translateX(-50%)',
                width: isActive ? 28 : 0, height: 2,
                background: '#00b37e',
                borderRadius: '0 0 3px 3px',
                transition: 'width 0.2s ease',
              }} />
              <span style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 26, borderRadius: 8,
                background: isActive ? '#e8f4ef' : 'transparent',
              }}>
                {item.icon}
              </span>
              {item.name}
            </NavLink>
          );
        })}

        {/* Botão Cadastros */}
        <button
          onClick={() => setDrawerOpen((prev) => !prev)}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 2, border: 'none', background: 'none',
            cursor: 'pointer',
            color: isCadastrosActive || drawerOpen ? '#00b37e' : '#9aada6',
            fontSize: '0.6rem',
            fontWeight: isCadastrosActive || drawerOpen ? 700 : 500,
            fontFamily: 'sans-serif',
            position: 'relative', paddingBottom: 2,
          }}
        >
          <span style={{
            position: 'absolute', top: 0, left: '50%',
            transform: 'translateX(-50%)',
            width: (isCadastrosActive || drawerOpen) ? 28 : 0, height: 2,
            background: '#00b37e',
            borderRadius: '0 0 3px 3px',
            transition: 'width 0.2s ease',
          }} />
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 26, borderRadius: 8,
            background: (isCadastrosActive || drawerOpen) ? '#e8f4ef' : 'transparent',
          }}>
            <Apps />
          </span>
          Cadastros
        </button>
      </nav>
    </>
  );
};

export default AgroSidebar;